import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { CountryCodes } from '@onevesthq/ov-enums';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import { Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

export const FETCH_ACCOUNT_TYPES = gql`
  query fetchAccountTypes {
    fetchAccountTypes{
      accountTypes {
        value
        translatedName{ ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

export const FETCH_ACCOUNT_TYPES_WITH_FILTER = gql`
  query fetchAccountTypes($input: FetchAccountTypesInput!) {
    fetchAccountTypes(input: $input){
      accountTypes {
        value
        translatedName{ ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

export const AccountTypeSelect = ({
  operator = '',
  onChange,
  value,
  label,
  size = 'small',
  omitAny = false,
  sortAlphebeticaly = false,
  onBlur,
  error,
  entityId,
  householdId,
  bypassFilteringByAvailableFeatureFlags = false,
  clearable = false,
  originOfAccount,
  testId,
}: {
  onChange: (event: any) => void;
  operator?: string;
  value?: string;
  label?: string;
  size?: 'small' | 'medium';
  omitAny?: boolean;
  bypassFilteringByAvailableFeatureFlags?: boolean;
  sortAlphebeticaly?: boolean;
  onBlur?: any;
  error?: any;
  entityId?: string;
  householdId?: string;
  clearable?: boolean;
  originOfAccount?: CountryCodes;
  testId?: string;
}) => {
  const { t } = useTranslation(['client', 'components']);
  const { organizationFeatureFlags } = useContext(UserContext);
  const {
    loading,
    error: er,
    data,
  } = useQuery(
    entityId || householdId || originOfAccount
      ? FETCH_ACCOUNT_TYPES_WITH_FILTER
      : FETCH_ACCOUNT_TYPES,
    {
      variables: {
        input: {
          filter: {
            entityId: entityId ?? undefined,
            householdId: householdId ?? undefined,
            country: originOfAccount ?? undefined,
          },
        },
      },
      fetchPolicy: 'no-cache',
    },
  );

  if (er)
    <Typography variant='bodySmall' component='p'>
      Error
    </Typography>;
  let accountTypesData: any[] = [];
  if (data) {
    if (bypassFilteringByAvailableFeatureFlags) {
      accountTypesData = [...data.fetchAccountTypes.accountTypes];
    } else {
      accountTypesData = accountTypesData.concat(
        data.fetchAccountTypes.accountTypes.filter((accountType: any) =>
          organizationFeatureFlags.has(accountType.value),
        ),
      );
    }
    if (
      sortAlphebeticaly &&
      accountTypesData !== undefined &&
      accountTypesData.length > 0
    ) {
      accountTypesData = accountTypesData.sort((a: any, b: any) =>
        translateBackend(a.translatedName).localeCompare(
          translateBackend(b.translatedName),
        ),
      );
    }
  }

  const isMultiple = ['in', 'notIn'].includes(operator);
  const selectValue = isMultiple
    ? Array.isArray(value)
      ? value
      : [value]
    : (value ?? 'ANY');
  const resolvedTestId = testId ?? 'account-type-select';

  return (
    <SelectField
      multiple={isMultiple}
      clearable={clearable}
      testId={resolvedTestId}
      label={label ?? t('client:accountType')}
      fullWidth
      value={selectValue}
      onChange={(e: any) => onChange(e.target.value)}
      size={size}
      onBlur={onBlur}
      error={error}
    >
      {!omitAny && (
        <MenuItem value='ANY' testId={`${resolvedTestId}-any-menu-item`}>
          {t('components:any')}
        </MenuItem>
      )}
      {loading ? (
        <MenuItem>...</MenuItem>
      ) : (
        accountTypesData.map((a: any) => (
          <MenuItem
            key={a.value}
            value={a.value}
            testId={`${resolvedTestId}-${kebabCase(a.value)}-menu-item`}
          >
            {translateBackend(a.translatedName) || a.value}
          </MenuItem>
        ))
      )}
    </SelectField>
  );
};
